import React, { useState } from 'react';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuditLogs } from '@/hooks/api/useAuditLogs';
import { useDebounce, useUrlTable } from '@/hooks';
import { AUDIT_EVENT_LABELS } from '@/api/audit';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { formatDateTime } from '@/lib/date';

interface TableState {
  page: number;
  limit: number;
  search?: string;
  eventType?: string;
  actorType?: string;
  dateRange?: string;
}

export default function AuditLog() {
  const { state, setPage, setSearch, setFilter } = useUrlTable<TableState>({
    page: 1,
    limit: 25,
    search: '',
    eventType: '',
    actorType: '',
    dateRange: 'all',
  });

  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const debouncedSearch = useDebounce(state.search, 300);

  const { data, isLoading } = useAuditLogs({
    page: state.page,
    limit: state.limit,
    search: debouncedSearch || undefined,
    eventType: state.eventType || undefined,
    actorType: state.actorType || undefined,
    dateRange: state.dateRange !== 'all' ? state.dateRange : undefined,
  });

  const totalPages = data ? data.totalPages : 0;

  const getEventLabel = (eventType: string) => {
    return AUDIT_EVENT_LABELS[eventType] || eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const toggleRow = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const eventTypeOptions = Object.entries(AUDIT_EVENT_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Audit Log</h1>
        <p className="text-muted-foreground">Track all system events and admin actions</p>
      </div>

      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        {/* Search and Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or IP address..."
              value={state.search || ''}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select
            value={state.eventType || 'all'}
            onValueChange={(value) => setFilter('eventType', value === 'all' ? undefined : value)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Event type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All event types</SelectItem>
              {eventTypeOptions.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={state.actorType || 'all'}
            onValueChange={(value) => setFilter('actorType', value === 'all' ? undefined : value)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Actor type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actors</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="superadmin">Super Admin</SelectItem>
              <SelectItem value="system">System</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={state.dateRange || 'all'}
            onValueChange={(value) => setFilter('dateRange', value)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {isLoading ? (
          <TableSkeleton
            columns={5}
            rows={10}
            headers={['Timestamp', 'Event', 'Actor', 'IP Address', 'Status']}
          />
        ) : !data || data.data.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No audit log entries found</p>
            {(state.search || state.eventType || state.actorType || (state.dateRange && state.dateRange !== 'all')) && (
              <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters</p>
            )}
          </div>
        ) : (
          <>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((entry) => (
                    <React.Fragment key={entry._id}>
                      <TableRow
                        className={`cursor-pointer hover:bg-muted ${expandedRow === entry._id ? 'bg-muted' : ''}`}
                        onClick={() => toggleRow(entry._id)}
                      >
                        <TableCell className="w-8 px-2">
                          {entry.details && Object.keys(entry.details).length > 0 ? (
                            expandedRow === entry._id ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )
                          ) : null}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                          {formatDateTime(entry.createdAt)}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-foreground">
                            {getEventLabel(entry.eventType)}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {entry.actorName ? (
                            <div>
                              <div className="text-sm font-medium text-foreground">{entry.actorName}</div>
                              {entry.email && (
                                <div className="text-xs text-muted-foreground">{entry.email}</div>
                              )}
                            </div>
                          ) : (
                            entry.email || <span className="text-muted-foreground italic">System</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono text-sm">
                          {entry.ipAddress || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={entry.success ? 'success' : 'error'}>
                            {entry.success ? 'Success' : 'Failed'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                      {expandedRow === entry._id && entry.details && Object.keys(entry.details).length > 0 && (
                        <TableRow key={`${entry._id}-details`} className="bg-muted">
                          <TableCell colSpan={6} className="px-6 py-3">
                            <div className="text-sm">
                              <p className="font-medium text-foreground mb-2">Details</p>
                              <pre className="text-xs text-muted-foreground bg-background border border-border rounded p-3 overflow-auto max-h-40">
                                {JSON.stringify(entry.details, null, 2)}
                              </pre>
                              {entry.userAgent && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  <span className="font-medium">User Agent:</span> {entry.userAgent}
                                </p>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(state.page - 1) * state.limit + 1} to{' '}
                  {Math.min(state.page * state.limit, data.total)} of {data.total} entries
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(state.page - 1)}
                    disabled={state.page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {state.page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(state.page + 1)}
                    disabled={state.page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
